import React, { useEffect, useState, useRef } from 'react';
import { api } from '../api';
import favConfig from '../config/spotifyFavorites.json';

// Convert new format to old format for backward compatibility
const favIds = favConfig.map(f => typeof f === 'string' ? f : f.id);
const favSlots = favConfig.reduce((acc, f) => {
  if (typeof f === 'object') {
    acc[f.id] = f.defaultSlot;
  }
  return acc;
}, {});

/* ─── helpers ────────────────────────────────────────────── */
const iso3am = (isoStr) => {
  if (!isoStr) return '';
  const d = new Date(isoStr);
  d.setHours(3, 0, 0, 0);
  return d.toISOString().slice(0, 16);
};

/** Parse album OR track URL (or a naked 22-char ID). */
const parseSpotifyUrl = (url) => {
  if (!url) return null;
  const m = url.match(/(?:album|track)\/([A-Za-z0-9]{22})(\?|$)/);
  if (m) {
    const kind = url.includes('/track/') ? 'track' : 'album';
    return { id: m[1], kind };
  }
  if (/^[A-Za-z0-9]{22}$/.test(url.trim()))
    return { id: url.trim(), kind: 'album' };
  return null;
};

/* runtime API base — works in CRA prod & Vite dev */
const API_BASE =
  process.env.REACT_APP_API_BASE ||           // Render / CRA build
  import.meta?.env?.VITE_API_BASE || '';      // local Vite dev

/* shared input style */
const formInput = {
  width: '100%',
  padding: 8,
  height: 25,
  background: '#1e1e1e',
  color: '#fff',
  border: 'none',
  borderRadius: 6,
  fontFamily: 'inherit',
};

export default function SpotifyScheduling({ releaseId, defaultDate, spotifyUrl = '' }) {
  /* ───────── state ───────── */
  const [isLinked, setIsLinked]   = useState(null);
  const [playlists, setPlaylists] = useState([]);
  const [visiblePls, setVisiblePls] = useState([]);
  const [selected,  setSelected]  = useState([]);    // [{ id, slot }]
  const [albumUrl,  setAlbumUrl]  = useState(spotifyUrl || '');
  const [trackIdx,  setTrackIdx]  = useState(0);
  const [runAt,     setRunAt]     = useState(iso3am(defaultDate));
  const [status,    setStatus]    = useState('');
  const [jobs,      setJobs]      = useState([]);
  const [showJobs,  setShowJobs]  = useState(false);
  const [favoritesConfig, setFavoritesConfig] = useState(() => {
    // Initialize with default config, will be loaded from backend
    return favConfig.map(f => typeof f === 'string' ? { id: f, defaultSlot: 7 } : f);
  });
  const [showFavoritesManager, setShowFavoritesManager] = useState(false);
  const [favoritesLoaded, setFavoritesLoaded] = useState(false);
  const isInitialLoad = useRef(true);
  const hasLoadedFromBackend = useRef(false);
  const initialFavoritesConfig = useRef(null);

  // If a Spotify URL is provided (or changes), seed the field if empty
useEffect(() => {
  if (spotifyUrl && !albumUrl) setAlbumUrl(spotifyUrl);
}, [spotifyUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ───────── load favorites config from backend ───────── */
  useEffect(() => {
    (async () => {
      try {
        const res = await api('/api/spotify/favorites');
        if (res.ok) {
          const config = await res.json();
          if (Array.isArray(config)) {
            // Always use what backend returns
            // If empty array and it's the first time (no saved config), keep default
            // If empty array and user previously saved it, use empty (user cleared all)
            // If non-empty, use it
            console.log('[Favorites] Loaded from backend:', config.length, 'items');
            setFavoritesConfig(config);
            hasLoadedFromBackend.current = true;
          }
        } else {
          console.error('[Favorites] Failed to load:', res.status, await res.text());
        }
      } catch (err) {
        console.error('[Favorites] Failed to load favorites config:', err);
      } finally {
        setFavoritesLoaded(true);
        // Mark that initial load is complete after a delay to ensure state has updated
        setTimeout(() => {
          isInitialLoad.current = false;
        }, 1000);
      }
    })();
  }, []);

  /* ───────── auto-save favorites config to backend ───────── */
  useEffect(() => {
    // Only save if:
    // 1. Favorites have been loaded from backend
    // 2. It's not the initial load period
    // 3. We've actually loaded from backend (to avoid saving default config on first mount)
    if (!favoritesLoaded || isInitialLoad.current || !hasLoadedFromBackend.current) {
      return;
    }
    
    // Debounce saves to avoid too many API calls
    const timeoutId = setTimeout(async () => {
      try {
        console.log('[Favorites] Saving to backend:', favoritesConfig.length, 'items');
        const res = await api('/api/spotify/favorites', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ config: favoritesConfig }),
        });
        if (res.ok) {
          const result = await res.json();
          console.log('[Favorites] Saved successfully:', result);
        } else {
          const errorText = await res.text();
          console.error('[Favorites] Failed to save:', res.status, errorText);
        }
      } catch (err) {
        console.error('[Favorites] Failed to save favorites config:', err);
      }
    }, 500);
    
    return () => clearTimeout(timeoutId);
  }, [favoritesConfig, favoritesLoaded]);

  /* ───────── fetch playlists + existing jobs ───────── */
  useEffect(() => {
    (async () => {
      try {
        const plRes = await api('/api/spotify/playlists');
        if (plRes.status === 401) { setIsLinked(false); return; }
        const pls = await plRes.json();
        setPlaylists(pls);
        const currentFavIds = favoritesConfig.map(f => f.id);
        setVisiblePls(pls.filter(p => currentFavIds.includes(p.id)));
        setIsLinked(true);

        if (releaseId) {
          const j = await api(`/api/spotify/schedule/${releaseId}`).then(r => r.json());
          setJobs(j);
        }
      } catch {
        setStatus('Error loading data');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [releaseId]);

  /* ───────── update visible playlists when favorites config changes ───────── */
  useEffect(() => {
    // Only update if we have playlists loaded and we're not showing all
    if (playlists.length === 0) return;
    
    // Check if we're currently showing all playlists
    const isShowingAll = visiblePls.length === playlists.length;
    if (isShowingAll) return; // Don't update if showing all
    
    const currentFavIds = favoritesConfig.map(f => f.id);
    setVisiblePls(playlists.filter(p => currentFavIds.includes(p.id)));
  }, [favoritesConfig, playlists, visiblePls.length]);

  /* ───────── playlist helpers ───────── */
  const toggleSelect = (id) => {
    // Get default slot from favorites config, fallback to 7
    const favEntry = favoritesConfig.find(f => f.id === id);
    const defaultSlot = favEntry ? favEntry.defaultSlot : 7;
    
    setSelected(prev =>
      prev.some(o => o.id === id)
        ? prev.filter(o => o.id !== id)
        : [...prev, { id, slot: defaultSlot }]
    );
  };
  
  /* ───────── favorites management ───────── */
  const toggleFavorite = (id) => {
    setFavoritesConfig(prev => {
      const exists = prev.find(f => f.id === id);
      if (exists) {
        return prev.filter(f => f.id !== id);
      } else {
        return [...prev, { id, defaultSlot: 7 }];
      }
    });
  };
  
  const updateFavoriteSlot = (id, slot) => {
    setFavoritesConfig(prev =>
      prev.map(f => {
        if (f.id === id) {
          // Ensure slot is between 0 and 7
          const newSlot = Math.max(0, Math.min(7, Number(slot)));
          return { ...f, defaultSlot: newSlot };
        }
        return f;
      })
    );
  };
  
  const saveFavoritesConfig = async () => {
    try {
      // Update visible playlists if not showing all
      const currentFavIds = favoritesConfig.map(f => f.id);
      if (!showingAll) {
        setVisiblePls(playlists.filter(p => currentFavIds.includes(p.id)));
      }
      // If showing all, keep showing all - don't reset
      
      setStatus('✅ Favorites configuration saved!');
      setTimeout(() => setStatus(''), 3000);
      setShowFavoritesManager(false);
    } catch (err) {
      setStatus('⚠️ Failed to save favorites configuration');
    }
  };

  const changeSlot = (id, slot) =>
    setSelected(prev => prev.map(o => o.id === id ? { ...o, slot } : o));

  const cardStyle = sel => ({
    width: 110,
    cursor: 'pointer',
    borderRadius: 8,
    overflow: 'hidden',
    border: sel ? '3px solid #1DB954' : '0 solid #444',
    transition: 'border .15s',
  });

  /* ───────── delete helper ───────── */
  const deleteJob = async (jobId) => {
    try {
      await api(`/api/spotify/schedule/${jobId}`, { method: 'DELETE' });
      setJobs(j => j.filter(x => x._id !== jobId));
    } catch {
      alert('Failed to delete');
    }
  };

  /* ───────── connect handler ───────── */
  const gotoSpotifyLogin = () =>
    (window.location.href = `${API_BASE}/api/spotify/login`);

  /* ───────── guards ───────── */
  if (isLinked === null) return <p style={{ color: '#ccc' }}>Checking Spotify…</p>;
  if (!isLinked)
    return (
      <button
        onClick={gotoSpotifyLogin}
        style={{
          padding: '10px 20px',
          borderRadius: 6,
          background: '#1DB954',
          border: 'none',
          color: '#fff',
          cursor: 'pointer',
        }}
      >
        Connect to Spotify
      </button>
    );

  const showingAll = visiblePls.length === playlists.length;

  /* ───────── JSX ───────── */
  return (
    <div style={{ maxWidth: 920 }}>
      {/* ─── playlist grid ─── */}
      <label style={{ fontWeight: 'bold' }}>Playlists (+ slot):</label>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 12,
          maxHeight: 240,
          overflowY: 'auto',
          margin: '8px 0 6px',
          paddingRight: 4,
        }}
      >
        {visiblePls.map((p) => {
          const selObj = selected.find(o => o.id === p.id);
          const sel    = Boolean(selObj);
          const isScheduled = jobs.some(j => j.playlistId === p.id);
          return (
            <div
              key={p.id}
              style={{ ...cardStyle(sel), position: 'relative' }}
              onClick={() => toggleSelect(p.id)}
            >
              {/* Scheduled checkmark indicator */}
              {isScheduled && (
                <div
                  style={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    width: 20,
                    height: 20,
                    borderRadius: '50%',
                    background: '#1DB954',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 10,
                    boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                  }}
                  title="Already scheduled"
                >
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#fff"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              )}
              <img
                src={p.image || 'https://placehold.co/300x300?text=No+Cover'}
                alt={p.name}
                style={{ width: '100%', aspectRatio: '1 / 1', objectFit: 'cover' }}
              />
              <div
                style={{
                  padding: 6,
                  fontSize: 12,
                  background: '#111',
                  textAlign: 'center',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {p.name}
              </div>
              {sel && (
                <input
                  type="number"
                  min="0"
                  value={selObj.slot}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => changeSlot(p.id, Number(e.target.value))}
                  style={{
                    width: '100%',
                    border: 'none',
                    borderTop: '1px solid #333',
                    padding: '4px 6px',
                    background: '#1e1e1e',
                    color: '#fff',
                    fontSize: 12,
                    textAlign: 'center',
                    boxSizing: 'border-box',
                  }}
                />
              )}
            </div>
          );
        })}
      </div>

      {/* favourites/all toggle + manage favorites */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 20 }}>
        <button
          onClick={() => {
            const currentFavIds = favoritesConfig.map(f => f.id);
            if (showingAll) {
              // Hide other playlists - show only favorites
              setVisiblePls(playlists.filter(p => currentFavIds.includes(p.id)));
            } else {
              // Show all playlists
              setVisiblePls(playlists);
            }
          }}
          style={{
            background: 'none',
            color: '#1DB954',
            border: 'none',
            cursor: 'pointer',
            display: 'block',
            width: 'fit-content',
          }}
        >
          {showingAll ? '− Hide other playlists' : '+ Show other playlists'}
        </button>
        
        <button
          onClick={() => {
            if (!showFavoritesManager) {
              // Save current state before opening manager
              initialFavoritesConfig.current = JSON.parse(JSON.stringify(favoritesConfig));
            }
            setShowFavoritesManager(!showFavoritesManager);
          }}
          style={{
            padding: '6px 12px',
            background: showFavoritesManager ? '#1DB954' : '#444',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            cursor: 'pointer',
            fontSize: '0.9rem',
          }}
        >
          {showFavoritesManager ? 'Hide' : 'Manage'} Favorites
        </button>
      </div>
      
      {/* Favorites Manager */}
      {showFavoritesManager && (
        <div style={{
          background: '#1e1e1e',
          padding: 16,
          borderRadius: 8,
          marginBottom: 20,
          border: '1px solid #333',
        }}>
          <h3 style={{ margin: '0 0 12px 0', fontSize: '1rem' }}>Manage Favorite Playlists</h3>
          <p style={{ margin: '0 0 16px 0', color: '#999', fontSize: '0.85rem' }}>
            Select playlists to show as favorites by default. Set their default slot position.
          </p>
          
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 10,
            maxHeight: 200,
            overflowY: 'auto',
            marginBottom: 16,
            padding: 8,
            background: '#0f0f0f',
            borderRadius: 6,
          }}>
            {playlists.map(p => {
              const isFavorite = favoritesConfig.some(f => f.id === p.id);
              const favEntry = favoritesConfig.find(f => f.id === p.id);
              
              return (
                <div
                  key={p.id}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: 100,
                    borderRadius: 6,
                    overflow: 'hidden',
                    border: isFavorite ? '2px solid #1DB954' : '2px solid #333',
                    cursor: 'pointer',
                    background: isFavorite ? '#1a1a1a' : '#0f0f0f',
                  }}
                  onClick={() => toggleFavorite(p.id)}
                >
                  <img
                    src={p.image || 'https://placehold.co/300x300?text=No+Cover'}
                    alt={p.name}
                    style={{ width: '100%', aspectRatio: '1 / 1', objectFit: 'cover', opacity: isFavorite ? 1 : 0.5 }}
                  />
                  <div style={{
                    padding: 4,
                    fontSize: 10,
                    background: '#111',
                    textAlign: 'center',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}>
                    {p.name}
                  </div>
                  {isFavorite && (
                    <div 
                      style={{ padding: 4, background: '#1e1e1e' }}
                      onClick={(e) => e.stopPropagation()}
                      onMouseDown={(e) => e.stopPropagation()}
                    >
                      <label style={{ fontSize: 9, color: '#999', display: 'block', marginBottom: 2 }}>
                        Default Slot:
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="7"
                        step="1"
                        value={favEntry?.defaultSlot !== undefined ? favEntry.defaultSlot : 7}
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                        onChange={(e) => {
                          e.stopPropagation();
                          const val = e.target.value;
                          // Convert to number, default to 0 if empty or invalid
                          const numVal = val === '' ? 0 : Number(val);
                          if (!isNaN(numVal) && numVal >= 0 && numVal <= 7) {
                            updateFavoriteSlot(p.id, numVal);
                          }
                        }}
                        onFocus={(e) => {
                          e.stopPropagation();
                          e.target.select(); // Select all text for easy editing
                        }}
                        style={{
                          width: '100%',
                          padding: 2,
                          background: '#2a2a2a',
                          color: '#fff',
                          border: 'none',
                          borderRadius: 3,
                          fontSize: 11,
                          textAlign: 'center',
                        }}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button
              onClick={() => {
                // Restore to the state when manager was opened
                if (initialFavoritesConfig.current) {
                  setFavoritesConfig(initialFavoritesConfig.current);
                }
                setShowFavoritesManager(false);
              }}
              style={{
                padding: '8px 16px',
                background: '#444',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              onClick={saveFavoritesConfig}
              style={{
                padding: '8px 16px',
                background: '#1DB954',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                cursor: 'pointer',
              }}
            >
              Save Favorites
            </button>
          </div>
        </div>
      )}

      {/* ─── album / track URL ─── */}
      <label style={{ fontWeight: 'bold' }}>Album / Track URL or ID:</label>
      <input
        value={albumUrl}
        onChange={(e) => setAlbumUrl(e.target.value)}
        placeholder="https://open.spotify.com/album/…  OR  /track/…"
        style={{ ...formInput, marginBottom: 12, width: '42%' }}
      />

      {/* ─── track index + run-at side by side ─── */}
      <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
        <div style={{ flex: '0 0 180px', minWidth: 180 }}>
          <label style={{ fontWeight: 'bold' }}>Track index (0 = first):</label>
          <input
            type="number"
            min="0"
            value={trackIdx}
            onChange={(e) => setTrackIdx(e.target.value)}
            style={{ ...formInput, marginBottom: 12 }}
          />
        </div>

        <div style={{ flex: '0 0 180px', minWidth: 180 }}>
          <label style={{ fontWeight: 'bold' }}>Run at (date / time):</label>
          <input
            type="datetime-local"
            value={runAt}
            onChange={(e) => setRunAt(e.target.value)}
            className="runAtInput"
            style={{ ...formInput, marginBottom: 12 }}
          />
        </div>
      </div>

      {/* ─── save ─── */}
      <button
        onClick={async () => {
          setStatus('');
          const parsed = parseSpotifyUrl(albumUrl);
          if (!parsed) {
            setStatus('⚠️ Invalid Spotify URL/ID');
            return;
          }
          if (!selected.length || !runAt) {
            setStatus('⚠️ Select playlist(s) and time');
            return;
          }
          try {
            const res = await api('/api/spotify/schedule', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                releaseId,
                playlistIds: selected.map(o => o.id),
                slots:       selected.map(o => o.slot),
                albumId:     parsed.id,
                albumKind:   parsed.kind,
                trackIndex:  Number(trackIdx),
                runAt,
              }),
            });
            if (!res.ok) throw new Error();
            setStatus('✅ Schedule saved!');
            const j = await api(`/api/spotify/schedule/${releaseId}`).then(r => r.json());
            setJobs(j);
          } catch {
            setStatus('Error saving schedule');
          }
        }}
        disabled={!(selected.length && albumUrl && runAt)}
        style={{
          padding: '10px 24px',
          borderRadius: 6,
          background: selected.length && albumUrl && runAt ? '#1DB954' : '#555',
          border: 'none',
          color: '#fff',
          cursor: selected.length && albumUrl && runAt ? 'pointer' : 'not-allowed',
        }}
      >
        Save Schedule
      </button>

      {/* ─── collapsible scheduled jobs ─── */}
      {jobs.length > 0 && (
        <div style={{ marginTop: 20 }}>
          <div
            onClick={() => setShowJobs(!showJobs)}
            style={{
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              fontWeight: 'bold',
              color: '#1DB954',
            }}
          >
            <span>Scheduled ✅</span>
            <span>{showJobs ? '▲' : '▼'}</span>
          </div>
          {showJobs && (
            <ul style={{ paddingLeft: 20, fontSize: 14, marginTop: 8 }}>
              {jobs.map(j => {
                const pl = playlists.find(p => p.id === j.playlistId);
                const name = pl ? pl.name : j.playlistId;
                const when = new Date(j.runAt).toLocaleString(undefined, {
                  dateStyle: 'short',
                  timeStyle: 'short',
                  hour12: false,
                     timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone, // force local TZ
                });
                return (
                  <li
                    key={j._id}
                    style={{
                      marginBottom: 4,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                    }}
                  >
                    <span>
                      <strong>{name}</strong> • slot&nbsp;{j.slot} • {when}
                    </span>
                    <button
                      onClick={() => deleteJob(j._id)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#f66',
                        cursor: 'pointer',
                        fontWeight: 'bold',
                      }}
                    >
                      ✕
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      {status && (
        <p
          style={{
            marginTop: 12,
            color: status.startsWith('✅') ? '#4caf50' : '#f66',
          }}
        >
          {status}
        </p>
      )}

      {/* white calendar icon */}
      <style>{`
        .runAtInput::-webkit-calendar-picker-indicator{filter:invert(1);}
        label{display:block;margin-bottom:10px;}
      `}</style>
    </div>
  );
}